/**
 * DEPOSIT MANAGEMENT - Info Deposit & Request untuk Penjaga Loket
 */

import React, { useState, useEffect } from 'react';
import { Wallet, Plus, History, CheckCircle, Clock, XCircle, AlertCircle, Upload, FileText, RefreshCw, X } from 'lucide-react';

const DepositManagement = ({ api, user }) => {
  const [deposits, setDeposits] = useState([]);
  const [riwayat, setRiwayat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    deposit_id: '',
    jumlah_topup: '',
    bank_tujuan: 'BRIS',
    keterangan: ''
  });

  useEffect(() => {
    loadData();
    loadRiwayat();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/deposits');
      setDeposits(data);
    } catch (err) {
      console.error('Gagal memuat deposit:', err);
      setError('Gagal memuat data deposit: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadRiwayat = async () => {
    if (!user?.loket_id) return;
    try {
      const data = await api.get(`/api/deposit-topup-requests/riwayat/${user.loket_id}?limit=20`);
      setRiwayat(data);
    } catch (err) {
      console.error('Gagal memuat riwayat:', err);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        deposit_id: formData.deposit_id,
        jumlah_topup: parseFloat(formData.jumlah_topup) || 0,
        keterangan: formData.keterangan,
        bank_tujuan: formData.bank_tujuan
      };

      await api.post('/api/deposit-topup-requests', payload);
      setSuccess('Request top-up saldo bank berhasil dikirim! Menunggu persetujuan Manager/Owner untuk menentukan jumlah.');
      setShowRequestModal(false);
      setFormData({ deposit_id: '', jumlah_topup: '', bank_tujuan: 'BRIS', keterangan: '' });

      // Reload riwayat setelah request berhasil
      await loadRiwayat();

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Gagal membuat request deposit');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING_MANAGER': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'APPROVED': 'bg-blue-100 text-blue-800 border-blue-300',
      'REJECTED': 'bg-red-100 text-red-800 border-red-300',
      'COMPLETED': 'bg-green-100 text-green-800 border-green-300'
    };
    const labels = {
      'PENDING_MANAGER': '⏳ Menunggu Approve',
      'APPROVED': '✓ Disetujui',
      'REJECTED': '✗ Ditolak',
      'COMPLETED': '✓ Selesai'
    };
    return (
      <span className={`px-3 py-1 text-xs rounded-full border ${badges[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatRupiah = (num) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);
  };

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <div className="flex items-center">
            <CheckCircle className="text-green-500 mr-2" size={20} />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-2" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* INFO DEPOSIT */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet className="text-purple-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Info Saldo Bank</h2>
          </div>
          <button
            onClick={loadData}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Memuat data deposit...</div>
        ) : deposits.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Wallet size={48} className="mx-auto mb-2 text-gray-400" />
            <p>Belum ada deposit provider</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deposits.map(deposit => (
              <div key={deposit.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{deposit.provider_nama}</h3>
                  <div className="text-2xl font-bold text-purple-600">
                    {formatRupiah(deposit.saldo)}
                  </div>
                </div>
                <p className="text-sm text-gray-500">Saldo Deposit Tersedia</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setShowRequestModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow"
          >
            <Plus size={20} />
            Request Top-up Saldo
          </button>
        </div>
      </div>

      {/* RIWAYAT DEPOSIT */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Riwayat Request Top-up</h2>
          </div>
          <button
            onClick={loadRiwayat}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {riwayat.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History size={48} className="mx-auto mb-2 text-gray-400" />
            <p>Belum ada riwayat request deposit</p>
          </div>
        ) : (
          <div className="space-y-3">
            {riwayat.map(req => (
              <div key={req.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{req.bank_nama || 'Bank Tujuan: ' + (req.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri')}</h4>
                      {getStatusBadge(req.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Jumlah: </span>
                        {formatRupiah(req.jumlah_topup)}
                      </div>
                      <div>
                        <span className="font-medium">Bank Tujuan: </span>
                        {req.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                      </div>
                      <div>
                        <span className="font-medium">Requester: </span>
                        {req.requested_by_nama}
                      </div>
                      <div>
                        <span className="font-medium">Tanggal: </span>
                        {new Date(req.requested_at).toLocaleString('id-ID')}
                      </div>
                    </div>
                    {req.keterangan && (
                      <div className="mt-2 text-sm text-gray-500">
                        <span className="font-medium">Keterangan: </span>{req.keterangan}
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline Approval */}
                <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                  {req.status === 'PENDING_MANAGER' && (
                    <div className="flex items-center gap-1 text-yellow-600">
                      <Clock size={14} />
                      Menunggu persetujuan Manager/Owner
                    </div>
                  )}
                  {req.status === 'APPROVED' && (
                    <div className="flex items-center gap-1 text-blue-600">
                      <CheckCircle size={14} />
                      Disetujui oleh {req.approved_by_nama} - Menunggu eksekusi Kasir
                    </div>
                  )}
                  {req.status === 'REJECTED' && (
                    <div className="flex items-center gap-1 text-red-600">
                      <XCircle size={14} />
                      Ditolak oleh {req.approved_by_nama}
                      {req.rejection_note && `: ${req.rejection_note}`}
                    </div>
                  )}
                  {req.status === 'COMPLETED' && (
                    <div className="flex items-center gap-1 text-green-600">
                      <CheckCircle size={14} />
                      Selesai - Saldo telah ditambahkan
                      {req.bank_tujuan && ` (Saldo ${req.bank_tujuan} telah diupdate)`}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* REQUEST DEPOSIT MODAL */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-purple-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus size={20} />
                Request Top-up Saldo Bank
              </h3>
              <button
                onClick={() => setShowRequestModal(false)}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deposit Provider *
                </label>
                <select
                  value={formData.deposit_id}
                  onChange={(e) => setFormData({ ...formData, deposit_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                >
                  <option value="">-- Pilih Deposit Provider --</option>
                  {deposits.map(dep => (
                    <option key={dep.id} value={dep.id}>
                      {dep.provider_nama} - Saldo: {formatRupiah(dep.saldo)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jumlah Top-up (Rp) *
                </label>
                <input
                  type="number"
                  value={formData.jumlah_topup}
                  onChange={(e) => setFormData({ ...formData, jumlah_topup: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Masukkan jumlah top-up"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Tujuan *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, bank_tujuan: 'BRIS' })}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      formData.bank_tujuan === 'BRIS'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <div className="font-semibold">💚 BSI</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, bank_tujuan: 'MANDIRI' })}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      formData.bank_tujuan === 'MANDIRI'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold">💙 Bank Mandiri</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keterangan
                </label>
                <textarea
                  value={formData.keterangan}
                  onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows="3"
                  placeholder="Alasan request deposit (opsional)"
                />
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded">
                <p className="text-sm text-purple-800">
                  <strong>Info:</strong> Request top-up saldo bank akan memerlukan persetujuan Manager/Owner.
                  Manager/Owner akan menentukan jumlah top-up.
                  Setelah disetujui, Kasir akan memproses dan upload bukti transfer.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowRequestModal(false);
                    setFormData({ bank_tujuan: 'BRIS', keterangan: '' });
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
                >
                  {submitting ? 'Mengirim...' : 'Kirim Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositManagement;
